import { Grid, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { REPORT_LEVEL_OPTIONS } from "../../../../utils/technicalReportLevel";
import { ADMIN_COMPANY_SELECT_OPTIONS } from "../../../../constants/discoCompanies";

/** Report Level + conditional Company/Circle/Division/Sub-Division filters (APMS-style). */
export default function TechnicalReportLevelFields({
  code,
  reportLevel,
  onReportLevelChange,
  availableReportLevelOptions = REPORT_LEVEL_OPTIONS,
  selectedCompanyCode,
  onChangeCompany,
  showCircleFilter,
  circleCode,
  onChangeCircle,
  filteredCircles = [],
  circleFilterEnabled = true,
  showDivisionFilter,
  selectedDivision,
  onChangeDivision,
  filteredDivisions = [],
  divisionFilterEnabled = false,
  showSubDivisionFilter,
  selectedSubDivision,
  onChangeSubDivision,
  filteredSubDivisions = [],
  subDivisionFilterEnabled = false,
  companySelectClassName = "dropdown-field cs-filter-input",
  hierarchySelectClassName = "dropdown-field cs-filter-input",
  includePitcCompanyOption = true,
  companyPlaceholder = "Select Company",
}) {
  const companyOptions = includePitcCompanyOption
    ? ADMIN_COMPANY_SELECT_OPTIONS
    : [{ value: "", label: companyPlaceholder }, ...ADMIN_COMPANY_SELECT_OPTIONS.filter((o) => o.value !== "10")];

  return (
    <>
      <Grid item xs={12} sm={6} md={2}>
        <Stack spacing={0.5}>
          <Typography className="field-label" component="label" variant="body2" sx={{ fontWeight: 600, color: "text.secondary", display: "block", minHeight: "1.35rem", lineHeight: 1.35, mb: 0.5 }}>
            Report Level
          </Typography>
          <select
            className={hierarchySelectClassName}
            style={{ width: "100%" }}
            value={reportLevel}
            onChange={onReportLevelChange}
            disabled={availableReportLevelOptions.length <= 1}
          >
            {availableReportLevelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Stack>
      </Grid>

      {String(code) === "10" && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <Typography className="field-label" component="label" variant="body2" sx={{ fontWeight: 600, color: "text.secondary", display: "block", minHeight: "1.35rem", lineHeight: 1.35, mb: 0.5 }}>Company</Typography>
            <select
              className={companySelectClassName}
              style={{ width: "100%" }}
              value={selectedCompanyCode}
              onChange={onChangeCompany}
            >
              {!includePitcCompanyOption && (
                <option value="">{companyPlaceholder}</option>
              )}
              {companyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Stack>
        </Grid>
      )}

      {showCircleFilter && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <Typography className="field-label" component="label" variant="body2" sx={{ fontWeight: 600, color: "text.secondary", display: "block", minHeight: "1.35rem", lineHeight: 1.35, mb: 0.5 }}>Circle</Typography>
            <select
              className={hierarchySelectClassName}
              style={{ width: "100%" }}
              value={circleCode || ""}
              onChange={onChangeCircle}
              disabled={!circleFilterEnabled}
            >
              <option value="">All Circle</option>
              {filteredCircles.map((circle) => (
                <option key={circle.circleCode} value={circle.circleCode}>
                  {circle.circleName}
                </option>
              ))}
            </select>
          </Stack>
        </Grid>
      )}

      {showDivisionFilter && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <Typography className="field-label" component="label" variant="body2" sx={{ fontWeight: 600, color: "text.secondary", display: "block", minHeight: "1.35rem", lineHeight: 1.35, mb: 0.5 }}>Division</Typography>
            <select
              className={hierarchySelectClassName}
              style={{ width: "100%" }}
              value={selectedDivision || ""}
              onChange={onChangeDivision}
              disabled={!divisionFilterEnabled}
            >
              <option value="">All Division</option>
              {filteredDivisions.map((division) => (
                <option key={division.divisionCode} value={division.divisionCode}>
                  {division.divisionName}
                </option>
              ))}
            </select>
          </Stack>
        </Grid>
      )}

      {showSubDivisionFilter && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <Typography className="field-label" component="label" variant="body2" sx={{ fontWeight: 600, color: "text.secondary", display: "block", minHeight: "1.35rem", lineHeight: 1.35, mb: 0.5 }}>Sub-Division</Typography>
            <select
              className={hierarchySelectClassName}
              style={{ width: "100%" }}
              value={selectedSubDivision || ""}
              onChange={onChangeSubDivision}
              disabled={!subDivisionFilterEnabled}
            >
              <option value="">All Sub-Division</option>
              {filteredSubDivisions.map((subDivision) => (
                <option
                  key={subDivision.subDivisionCode}
                  value={subDivision.subDivisionCode}
                >
                  {subDivision.subDivisionName}
                </option>
              ))}
            </select>
          </Stack>
        </Grid>
      )}
    </>
  );
}

TechnicalReportLevelFields.propTypes = {
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reportLevel: PropTypes.string,
  onReportLevelChange: PropTypes.func.isRequired,
  availableReportLevelOptions: PropTypes.array,
  selectedCompanyCode: PropTypes.string,
  onChangeCompany: PropTypes.func,
  showCircleFilter: PropTypes.bool,
  circleCode: PropTypes.string,
  onChangeCircle: PropTypes.func,
  filteredCircles: PropTypes.array,
  circleFilterEnabled: PropTypes.bool,
  showDivisionFilter: PropTypes.bool,
  selectedDivision: PropTypes.string,
  onChangeDivision: PropTypes.func,
  filteredDivisions: PropTypes.array,
  divisionFilterEnabled: PropTypes.bool,
  showSubDivisionFilter: PropTypes.bool,
  selectedSubDivision: PropTypes.string,
  onChangeSubDivision: PropTypes.func,
  filteredSubDivisions: PropTypes.array,
  subDivisionFilterEnabled: PropTypes.bool,
  companySelectClassName: PropTypes.string,
  hierarchySelectClassName: PropTypes.string,
  includePitcCompanyOption: PropTypes.bool,
  companyPlaceholder: PropTypes.string,
};
